import { DateTime } from 'luxon'

/**
 * Full IANA time-zone catalogue for the searchable company-timezone
 * picker (the "dynamic search" pattern — the user types their city and
 * the list narrows, rather than scrolling a curated shortlist).
 *
 * Sourced from `Intl.supportedValuesOf('timeZone')` (~418 canonical zones
 * on modern engines) so the list is always current and DST-correct — we
 * store the IANA id, never a fixed UTC offset, so daylight-saving resolves
 * automatically at display time. A small fallback keeps the picker usable
 * on any engine that lacks the API.
 *
 * NOTE: this is a separate concern from `ANCHOR_TIMEZONES` in
 * `components/CompanyTimezone.jsx`, which now exists ONLY to derive the
 * supported-country list for the Create Company picker.
 */

/** Format an offset in minutes-east-of-UTC as "UTC+5:30" / "UTC-8" /
 *  "UTC+0" — matches the label style the picker used for its old anchors. */
export function formatUtcOffset(mins) {
  const sign = mins < 0 ? '-' : '+'
  const abs = Math.abs(mins)
  const h = Math.floor(abs / 60)
  const m = abs % 60
  return `UTC${sign}${h}${m ? ':' + String(m).padStart(2, '0') : ''}`
}

/** Prettify an IANA id for display: "America/New_York" → "America/New York". */
function prettyName(zone) {
  return zone.replace(/_/g, ' ')
}

/** Minimal fallback if `Intl.supportedValuesOf` is unavailable — the same
 *  business hubs the picker shipped with before, so it never regresses to
 *  an empty list. */
const FALLBACK_ZONES = [
  'UTC',
  'America/Los_Angeles',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Athens',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Australia/Sydney',
]

const RAW_ZONES = (() => {
  try {
    if (typeof Intl.supportedValuesOf === 'function') {
      const list = Intl.supportedValuesOf('timeZone')
      if (Array.isArray(list) && list.length > 0) {
        // A few engines omit UTC from the list — guarantee it's offerable.
        return list.includes('UTC') ? list : ['UTC', ...list]
      }
    }
  } catch {
    // Fall through to the fallback below.
  }
  return FALLBACK_ZONES
})()

/** The picker's option list: `{ value: IANA id, name: prettified label,
 *  offset: current UTC-offset label }`. Kept in the native (alphabetical
 *  by IANA id) order — search is the primary way users find their zone,
 *  so a region-grouped alphabetical scan order is the least surprising.
 *  Computed once at module load. */
export const ALL_TIMEZONES = RAW_ZONES.map((value) => {
  const dt = DateTime.now().setZone(value)
  return {
    value,
    name: prettyName(value),
    offset: dt.isValid ? formatUtcOffset(dt.offset) : '',
  }
})
